import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { FaAngleRight, FaCheckCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteGoalItem,
  editGoalItem,
  getGoalCategories,
} from '../../stores/slices/goals/goalsActions';
import GoalComments from './GoalComments';
import { setSelectedGoal } from '../../stores/slices/goals/goalsSlice';
import { isIOSSafari } from '../../utils/isIOSSafari';
import { downloadIcsBlob } from '../../utils/downloadIcsBlob';
import * as ics from 'ics';
import EditGoalItem from './EditGoalItem';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GoGrabber } from 'react-icons/go';

const GoalItem = ({ goal, i, category }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: goal.id });

  const style = {
    transform: CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
    transition,
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const toast = useToast();
  const [openComments, setOpenComments] = useState(false);
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlertAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const [isMenuOpen, setIsMenuOpen] = useState({ state: false, id: null });
  const { selectedGoal } = useSelector(state => state.goals);
  const { userInfo } = useSelector(state => state.user);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef();
  const [isLoading, setIsLoading] = useState({ status: false, type: null });

  const updateGoal = async status => {
    setIsLoading({ status: true, type: status });
    await dispatch(
      editGoalItem({
        goalItem: goal.text,
        goalCategoryId: category.id,
        goalId: goal.id,
        goalStatus: status,
      })
    );
    await dispatch(getGoalCategories());
    setIsMenuOpen({ state: false, id: null });
    dispatch(setSelectedGoal(null));
    toast({
      description: 'Item has been updated',
      colorScheme: 'primary',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'bottom-right',
      variant: 'primary',
    });
  };

  const onDeleteItem = async () => {
    setIsDeleting(true);
    await dispatch(deleteGoalItem({ goalId: goal.id }));
    await dispatch(getGoalCategories());
    onCloseAlert();
    setIsDeleting(false);
    toast({
      description: 'Item has been deleted',
      colorScheme: 'primary',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'bottom-right',
      variant: 'primary',
    });
  };

  const downloadReminder = async (reminder, text) => {
    setIsLoading({ status: true, type: 'Reminder' });
    const dueDate = new Date(reminder.due);
    const { error, value } = ics.createEvent({
      start: [dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate()],
      title: text,
      organizer: { name: userInfo.full_name, email: userInfo.email },
      attendees: reminder.users.map(user => {
        return {
          name: user.full_name,
          email: user.email,
        };
      }),
    });
    setIsLoading({ false: true, type: 'Reminder' });
    if (error) {
      console.log(error);
      return;
    }

    let url = value;
    if (isIOSSafari()) {
      url = encodeURI(`data:text/calendar;charset=utf8,${value}`);
      window.open(url, '_blank');
      return;
    }

    downloadIcsBlob(url, `${text}.ics`);
  };
  return (
    <React.Fragment>
      <HStack
        w={'full'}
        pl={'3'}
        bg={i % 2 === 0 ? 'secondary' : 'transparent'}
        ref={setNodeRef}
        style={style}
      >
        <button {...listeners} {...attributes}>
          <GoGrabber className="h-6 w-6 text-black" />
        </button>
        <HStack
          w={'full'}
          pl={goal.unread_comments === true ? '3' : '9'}
          pr={'3'}
          bg={i % 2 === 0 ? 'secondary' : 'transparent'}
          justifyContent={'space-between'}
          key={goal.id}
        >
          {goal.unread_comments === true && (
            <Icon viewBox="0 0 200 200" color="primary">
              <path
                fill="currentColor"
                d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
              />
            </Icon>
          )}
          <Menu
            isOpen={
              isMenuOpen.id === goal.id &&
              isMenuOpen.state &&
              selectedGoal === goal.id
            }
            onClose={() => {
              setIsMenuOpen({ state: false, id: null });
              dispatch(setSelectedGoal(null));
            }}
            id={`goal-${goal.id}`}
          >
            <Text
              w={'full'}
              fontSize={'14px'}
              fontWeight={goal.unread_comments === true ? 'bold' : 'normal'}
              py={4}
              cursor="pointer"
              onClick={() => setOpenComments(true)}
              onContextMenu={e => {
                e.preventDefault();
                setIsMenuOpen(prev => {
                  return { ...prev, state: true, id: goal.id };
                });
                dispatch(setSelectedGoal(goal.id));

                const menu = document.querySelector(
                  `[id=menu-list-goal-${goal.id}]`
                );
                const bodyHeight = document.querySelector('body').clientHeight;
                const popper = menu.parentElement;

                const x = e.clientX;
                const y = e.clientY;

                Object.assign(popper.style, {
                  top:
                    y + popper.clientHeight > bodyHeight
                      ? `${y - popper.clientHeight}px`
                      : `${y}px`,
                  left: `${x}px`,
                });
              }}
            >
              {goal.text}
            </Text>
            <Portal>
              <MenuList
                onAnimationEnd={e => {
                  const menu = document.querySelector(
                    `[id=menu-list-goal-${goal.id}]`
                  );
                  menu.focus();
                }}
              >
                <MenuItem onClick={onOpen}>Edit</MenuItem>
                <MenuItem
                  onClick={() => {
                    onOpenAlertAlert(true);
                  }}
                >
                  Delete
                </MenuItem>
                <MenuItem onClick={() => setOpenComments(true)}>
                  Comment
                </MenuItem>
                {goal.status === 'In Progress' && (
                  <MenuItem
                    closeOnSelect={false}
                    justifyContent={'space-between'}
                    onClick={e => {
                      e.stopPropagation();
                      updateGoal('Complete');
                    }}
                  >
                    Complete{' '}
                    {isLoading.type === 'Complete' && (
                      <Spinner color="primary" size="sm" />
                    )}
                  </MenuItem>
                )}
                {(goal.status === 'Complete' || goal.status === 'Archived') && (
                  <MenuItem
                    closeOnSelect={false}
                    justifyContent={'space-between'}
                    onClick={e => {
                      e.stopPropagation();
                      updateGoal('In Progress');
                    }}
                  >
                    In Progress{' '}
                    {isLoading.type === 'In Progress' && (
                      <Spinner color="primary" size="sm" />
                    )}
                  </MenuItem>
                )}
                {goal && goal.status !== 'Archived' && (
                  <MenuItem
                    closeOnSelect={false}
                    justifyContent={'space-between'}
                    onClick={e => {
                      e.stopPropagation();
                      updateGoal('Archived');
                    }}
                  >
                    Archive{' '}
                    {isLoading.type === 'Archived' && (
                      <Spinner color="primary" size="sm" />
                    )}
                  </MenuItem>
                )}
                {goal && goal.reminder !== null && (
                  <MenuItem
                    closeOnSelect={false}
                    justifyContent={'space-between'}
                    onClick={e => {
                      e.stopPropagation();
                      downloadReminder(goal.reminder, goal.text);
                    }}
                  >
                    Download{' '}
                    {isLoading.type === 'Download' && (
                      <Spinner color="primary" size="sm" />
                    )}
                  </MenuItem>
                )}
              </MenuList>
            </Portal>
          </Menu>
          {goal.status === 'Complete' ? (
            <Icon as={FaCheckCircle} color={'primary'} />
          ) : null}
          <Tooltip label="Edit item" aria-label="Edit item">
            <IconButton
              variant={'outline'}
              size="xs"
              border={'1.5px solid'}
              borderColor={'lightGray.500'}
              p={0}
              color="lightGray.500"
              _hover={{ color: 'primary', borderColor: 'primary' }}
              icon={<FaAngleRight />}
              onClick={onOpen}
            />
          </Tooltip>
        </HStack>
      </HStack>
      {isOpen && (
        <EditGoalItem
          isOpen={isOpen}
          onClose={onClose}
          openComments={openComments}
          category={category}
          goal={goal}
        />
      )}

      {openComments && (
        <GoalComments
          setOpenComments={setOpenComments}
          goal={goal}
          openEdit={onOpen}
          goalCategoryId={category.id}
        />
      )}

      <AlertDialog
        isOpen={isOpenAlert}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlert}
        blockScrollOnMount={false}
        closeOnOverlayClick={isDeleting ? false : true}
        closeOnEsc={isDeleting ? false : true}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Item
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? If you delete this record, it'll be gone forever.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant={'outline'}
                onClick={onCloseAlert}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={() => onDeleteItem()}
                ml={3}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </React.Fragment>
  );
};

export default GoalItem;
